import { Text } from '@vibe/core';
import {
  Modal,
  ModalBasicLayout,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@vibe/core/next';
import React, { useRef, useState, useContext } from 'react';

interface ConfirmationDialogOptions {
  title?: string;
  content?: string;
  confirmButtonText?: string;
  cancelButtonText?: string;
}

interface ConfirmationDialogContextType {
  showConfirmation: (options?: ConfirmationDialogOptions) => Promise<boolean>;
}

const DEFAULT_OPTIONS = {
  title: 'Are you sure?',
  content:
    'Editing, adding, or deleting documents will restart any ongoing signing flows, potentially requiring you to re-sign the document.',
  confirmButtonText: 'Confirm',
  cancelButtonText: 'Cancel',
} as const;

export const ConfirmationDialogContext =
  React.createContext<ConfirmationDialogContextType>({
    showConfirmation: () => Promise.resolve(false),
  });

export const useConfirmationDialog = () => {
  const context = useContext(ConfirmationDialogContext);

  if (!context) {
    throw new Error(
      'useConfirmationDialog must be used within a ConfirmationDialogContextProvider',
    );
  }

  return context;
};

type Props = {
  children: React.ReactNode;
  id: string;
};

const ConfirmationDialogContextProvider = ({ id, children }: Props) => {
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [dialogOptions, setDialogOptions] =
    useState<ConfirmationDialogOptions>(DEFAULT_OPTIONS);
  const resolver = useRef<any>(null);

  const handleShow = (options?: ConfirmationDialogOptions) => {
    if (options) {
      setDialogOptions((prev) => ({
        ...DEFAULT_OPTIONS,
        ...prev,
        ...options,
      }));
    } else {
      setDialogOptions(DEFAULT_OPTIONS);
    }
    setShowConfirmationModal(true);

    return new Promise<boolean>(function (resolve) {
      resolver.current = resolve;
    });
  };

  const handleOk = () => {
    resolver.current && resolver.current(true);
    setShowConfirmationModal(false);
  };

  const handleCancel = () => {
    resolver.current && resolver.current(false);
    setShowConfirmationModal(false);
  };

  return (
    <ConfirmationDialogContext.Provider
      value={{ showConfirmation: handleShow }}
    >
      {children}
      <Modal
        show={showConfirmationModal}
        onClose={handleCancel}
        id={id}
        size="small"
      >
        <ModalBasicLayout>
          <ModalHeader title={dialogOptions.title || DEFAULT_OPTIONS.title} />
          <ModalContent>
            <Text type="text1" align="inherit" element="p">
              {dialogOptions.content || DEFAULT_OPTIONS.content}
            </Text>
          </ModalContent>
        </ModalBasicLayout>
        <ModalFooter
          primaryButton={{
            text:
              dialogOptions.confirmButtonText ||
              DEFAULT_OPTIONS.confirmButtonText,
            onClick: handleOk,
          }}
          secondaryButton={{
            text:
              dialogOptions.cancelButtonText ||
              DEFAULT_OPTIONS.cancelButtonText,
            onClick: handleCancel,
          }}
        />
      </Modal>
    </ConfirmationDialogContext.Provider>
  );
};

export default ConfirmationDialogContextProvider;
